import React, { useMemo } from 'react'
import clsx from 'clsx'
import { graphql } from 'gatsby'
import { Image } from '../image'
import { useSnippets } from '../snippet'

import * as styles from './collection-card.module.scss'

type Props = {
  className?: string
  collection: Queries.CollectionCardFragment
}

function numberDelimiter(x: number | null) {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function numberFormat(x: number | null) {
  const divisor = 1000000

  if (x && x >= divisor) {
    x = x / divisor
    return x?.toFixed(2) + 'm'
  } else {
    return numberDelimiter(x)
  }
}

export const CollectionCard = ({ className, collection }: Props) => {
  const s = useSnippets()
  const { collectionLink, loanVolume } = collection
  const Component = collectionLink ? 'a' : 'div'
  const componentProps = useMemo(() => {
    return collectionLink
      ? {
          href: collectionLink,
          className: clsx(styles.card, className),
          target: collectionLink ? '_blank' : '_self',
        }
      : {
          className: clsx(styles.card, className),
        }
  }, [collectionLink, className])

  return (
    <Component {...componentProps}>
      <div className={styles.image}>
        <Image
          image={collection.image}
          alt={collection.image?.title!}
          loading="lazy"
        />
      </div>
      <h3 className={styles.title}>{collection.title}</h3>
      <div className={styles.stats}>
        <dl>
          <dt>{s.avg_loan_size}</dt>
          <dd>
            {numberDelimiter(collection.avgLoanSize)} {s.currency_usd}
          </dd>
        </dl>
        <dl>
          <dt>{s.loan_volume}</dt>
          <dd>
            {numberFormat(loanVolume)} {s.currency_usd}
          </dd>
        </dl>
        <dl>
          <dt>{s.loan_count}</dt>
          <dd>{collection.loanCount}</dd>
        </dl>
        <dl>
          <dt>{s.apr}</dt>
          <dd>
            {collection.avgLoanDuration} {s.apr_unit}
          </dd>
        </dl>
      </div>
    </Component>
  )
}

export const CollectionFragments = graphql`
  fragment CollectionCardImage on ContentfulAsset {
    __typename
    id
    title
    gatsbyImageData(width: 255)
  }
  fragment CollectionCard on ContentfulCollection {
    __typename
    id
    title
    avgLoanDuration
    avgLoanSize
    loanVolume
    loanCount
    collectionLink
    image {
      ...CollectionCardImage
    }
  }
`
